import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, BarChart3, Calendar, Heart } from 'lucide-react';
import HealthStatusCards from '@/components/HealthStatusCards';
import { SmartNudgesBanner } from '@/components/SmartNudges';

/**
 * Simple Health Dashboard
 * Clean, mobile-first view of health status with quick actions
 */

const SimpleHealthDashboard = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-20">
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-8">
        
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center mb-3">
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-full p-3">
              <Heart className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Your Health Dashboard
          </h1>
          <p className="text-gray-600">A quick snapshot of your wellbeing</p>
        </div>

        {/* Smart Nudges Banner - Show only 1 prioritized nudge */}
        <SmartNudgesBanner />

        {/* Health Status Cards */}
        <HealthStatusCards />

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          
          {/* Daily Loop */}
          <Card className="border-2 border-purple-200 hover:border-purple-300 transition-colors cursor-pointer"onClick={() => navigate('/daily-loop')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-purple-100 rounded-full p-3">
                  <Calendar className="h-6 w-6 text-purple-600" />
                </div>
                <ArrowRight className="h-5 w-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">
                Daily Health Loop
              </h3>
              <p className="text-sm text-gray-600">
                Track your daily habits and earn rewards
              </p>
            </CardContent>
          </Card>

          {/* Progress Tracker */}
          <Card className="border-2 border-blue-200 hover:border-blue-300 transition-colors cursor-pointer" onClick={() => navigate('/progress')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-blue-100 rounded-full p-3">
                  <BarChart3 className="h-6 w-6 text-blue-600" />
                </div>
                <ArrowRight className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">
                Progress Tracker
              </h3>
              <p className="text-sm text-gray-600">
                See your patterns and get insights
              </p>
            </CardContent>
          </Card>

          {/* Quick Check */}
          <Card className="border-2 border-green-200 hover:border-green-300 transition-colors cursor-pointer" onClick={() => navigate('/quick-check')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-green-100 rounded-full p-3">
                  <Heart className="h-6 w-6 text-green-600" />
                </div>
                <ArrowRight className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">
                Quick Health Check
              </h3>
              <p className="text-sm text-gray-600">
                Log how you're feeling today
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Back to Main Dashboard */}
        <div className="flex justify-center">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="border-2 border-purple-300 hover:bg-purple-50"
          >
            Back to Full Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SimpleHealthDashboard;
